import {
  Badge,
  Button,
  Drawer,
  Empty,
  Flex,
  Popover,
  Typography,
} from "antd";
import { BellOutlined, CheckOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { NotificationEntity } from "../../features/notification/domain/entities/NotificationEntity.js";
import {
  useNotificationList,
  useNotificationMutations,
  useUnreadCount,
  useNotificationRealtime,
} from "../../features/notification/presentation/hooks/useNotifications.js";
import {
  routeForNotification,
  typeMeta,
} from "../../features/notification/presentation/notificationDisplay.js";
import { useAppState } from "../../core/app_state/AppStateContext.js";

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "vừa xong";
  if (m < 60) return `${m} phút trước`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} giờ trước`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} ngày trước`;
  return new Date(iso).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export function NotificationBell() {
  const { session } = useAppState();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 760);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 760px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useNotificationRealtime(session?.uid);

  const { data: unread = 0 } = useUnreadCount();
  const { data: items = [] } = useNotificationList({ limit: 10 });
  const { markRead, markAllRead } = useNotificationMutations();

  const openNotification = (n: NotificationEntity) => {
    if (!n.isRead) markRead.mutate(n.id);
    navigate(routeForNotification(n));
    setOpen(false);
  };

  const bell = (
    <span
      style={{ position: "relative", display: "inline-flex", alignItems: "center", cursor: "pointer" }}
      onClick={isMobile ? () => setOpen(true) : undefined}
    >
      <Badge count={unread} size="small" color="var(--red)" overflowCount={99}>
        <BellOutlined style={{ fontSize: 18, color: "var(--text2)" }} />
      </Badge>
    </span>
  );

  const content = (
    <div style={{ maxHeight: 380, overflowY: "auto" }}>
      {items.length === 0 ? (
        <Empty
          description="Không có thông báo"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: "12px 0" }}
        />
      ) : (
        <>
          <Flex vertical>
            {items.map((n: NotificationEntity) => {
              const meta = typeMeta(n.type);
              return (
                <div
                  key={n.id}
                  style={{
                    padding: "8px 4px",
                    cursor: "pointer",
                    background: n.isRead ? undefined : "rgba(255,255,255,0.03)",
                  }}
                  onClick={() => openNotification(n)}
                >
                  <div style={{ display: "flex", gap: 8, width: "100%" }}>
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 6,
                        marginTop: 6,
                        flexShrink: 0,
                        background: n.isRead ? "transparent" : "var(--red)",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Typography.Text style={{ fontSize: 13, display: "block" }} ellipsis>
                        {n.title}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12, display: "block" }} ellipsis>
                        {n.body}
                      </Typography.Text>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 2 }}>
                        <span style={{ fontSize: 11, color: meta.color }}>{meta.label}</span>
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                          {relativeTime(n.createdAt)}
                        </Typography.Text>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </Flex>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: 8,
              borderTop: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<CheckOutlined />}
              disabled={unread === 0}
              loading={markAllRead.isPending}
              onClick={() => markAllRead.mutate(undefined)}
            >
              Đánh dấu đã đọc
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                navigate("/notifications");
                setOpen(false);
              }}
            >
              Xem tất cả →
            </Button>
          </div>
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {bell}
        <Drawer
          title="Thông báo"
          placement="bottom"
          size="65vh"
          open={open}
          onClose={() => setOpen(false)}
          styles={{ body: { padding: "12px 16px" } }}
        >
          {content}
        </Drawer>
      </>
    );
  }

  return (
    <Popover
      content={<div style={{ width: "min(340px, calc(100vw - 24px))" }}>{content}</div>}
      title="Thông báo"
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      styles={{ root: { maxWidth: "calc(100vw - 24px)" } }}
      getPopupContainer={() => document.body}
    >
      {bell}
    </Popover>
  );
}
